import useStore from '@/store/use-store';
import { Box, Button, Divider, Drawer, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import logo from '../../_assets/logo.png';
import { MHidden } from '../../components/@material-extend';
import NavSection from '../../components/NavSection';
import Scrollbar from '../../components/Scrollbar';
import { PATH_ADMIN } from '../../routes/paths';
import adminSidebarConfig from './AdminSidebarConfig';
import apiSidebarConfig from './ApiSidebarConfig.jsx';
import authoritySidebarConfig from './AuthoritySidebarConfig';
import sidebarConfig from './SidebarConfig';

const DRAWER_WIDTH = 280;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH
  }
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: theme.spacing(0),
  marginBottom: theme.spacing(3),
  '& > div': {
    padding: theme.spacing(2),
    '& > div': {
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      fontSize: '0.9rem'
    }
  }
}));

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const user = useStore((state) => state.user);
  const isAdmin = user?.roles?.includes('ROLE_ADMIN');
  const isApi = user?.roles?.includes('ROLE_API');
  const isAuthority = user?.roles?.includes('ROLE_AUTHORITY');
  const { impersonatedUser } = useStore.getState();
  const { impersonateUser } = useStore((state) => state);
  const { t } = useTranslation();
  const navigate = useNavigate();

  let barConfig;
  if (isAdmin) {
    barConfig = adminSidebarConfig;
  } else if (isApi) {
    barConfig = apiSidebarConfig;
  } else {
    barConfig = isAuthority ? authoritySidebarConfig : sidebarConfig;
  }

  function onUnimpersonateClick() {
    impersonateUser('').then(() => {
      navigate(PATH_ADMIN.users);
    });
  }

  const handleNavigate = () => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
  };

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100%',
        '& .simplebar-content': { height: '100%', display: 'flex', flexDirection: 'column' }
      }}
    >
      <AccountStyle>
        <Box
          component="img"
          sx={{
            width: 200,
            marginTop: '20px',
            marginBottom: '20px'
          }}
          alt=""
          src={logo}
        />
        <Divider sx={{ borderBottomWidth: 1, width: '100%' }} />
        <div align="center">
          <Typography variant="h5">
            {user.firstName} {user.lastName}{' '}
          </Typography>
          {impersonatedUser && (
            <div>
              <Button onClick={onUnimpersonateClick}>{t('unimpersonate')}</Button>
            </div>
          )}
          {user.organization && (
            <Typography mt={0} key={user.organization.id} variant="caption">
              {user.organization.title}
            </Typography>
          )}
        </div>
        <Divider variant="middle" sx={{ borderBottomWidth: 1 }} />
      </AccountStyle>
      <NavSection navConfig={barConfig} onNavigate={handleNavigate} />
    </Scrollbar>
  );

  return (
    <RootStyle>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.default'
            }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
